import { AdminApiError, type AdminClient, type SicboWeightsOverride } from '@apx/admin-sdk';
import { sicboWeightsSchema } from '@apx/integrator-config/schemas';
import { askPrompt } from '../modal.js';
import { fmtDateTime } from '../format.js';

export interface SicboWeightsPageDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
  roundEngineBaseUrl: string;
}

const DIE_FACES = ['1', '2', '3', '4', '5', '6'] as const;

// Uniform fair die — mirrors DEFAULT_WEIGHTS in @apx/game-sicbo.
const DEFAULT_WEIGHTS: SicboWeightsOverride = {
  version: 1,
  die: { 1: 1 / 6, 2: 1 / 6, 3: 1 / 6, 4: 1 / 6, 5: 1 / 6, 6: 1 / 6 },
};

/**
 * Sicbo RNG editor — single-table die weights. Each of the three dice draws
 * independently over this distribution; faces 1..6 must sum to 1.0. A loaded
 * die shifts BIG/SMALL/total/triple odds, so changes ride the two-admin
 * approval flow. Live RTP (real settled rounds) shown for sanity-checking.
 */
export async function renderSicboWeightsPage(
  root: HTMLElement,
  deps: SicboWeightsPageDeps,
): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">RNG &amp; RTP — Sic Bo</h1>
    <p class="page-sub">Single fair-die distribution drawn three times per round. Faces 1–6 must sum to 1.0. A skewed die changes every derived bet (BIG/SMALL, totals, triples) at once — changes go through two-admin approval.</p>

    <div class="card">
      <h2>Die weights (sum must be 1.0)</h2>
      <div class="form-grid">${DIE_FACES.map(
        (f) => `<label>face ${f}
          <input data-die="${f}" type="number" min="0" max="1" step="0.0001" />
        </label>`,
      ).join('')}</div>
      <p class="muted" style="margin-top:8px"><span id="die-sum">sum: 0</span></p>
      <div class="row">
        <button id="sw-normalize" class="ghost">Auto-normalize</button>
        <button id="sw-uniform" class="ghost">Reset to fair die</button>
        <button id="sw-save" class="primary">Propose change</button>
        <span id="sw-msg" class="muted"></span>
      </div>
    </div>

    <div class="card">
      <h2>Pending approvals for this surface</h2>
      <div id="sw-pending"><p class="muted">Loading…</p></div>
    </div>
  `;

  const dieInputs = new Map<string, HTMLInputElement>();
  for (const f of DIE_FACES) {
    const el = root.querySelector<HTMLInputElement>(`input[data-die="${f}"]`);
    if (el) {
      dieInputs.set(f, el);
      // 'input' keeps the sum live as you type; 'change' clamps to [0,1] on
      // blur/commit so a fat-fingered 5 snaps back to 1 without fighting typing.
      el.addEventListener('input', updateSum);
      el.addEventListener('change', () => {
        const n = Number(el.value);
        if (Number.isFinite(n)) el.value = String(Math.min(1, Math.max(0, n)));
        updateSum();
      });
    }
  }
  const dieSumEl = root.querySelector<HTMLElement>('#die-sum')!;
  const msgEl = root.querySelector<HTMLElement>('#sw-msg')!;
  const saveBtn = root.querySelector<HTMLButtonElement>('#sw-save')!;

  function sumOf(): number {
    let sum = 0;
    for (const input of dieInputs.values()) sum += Number(input.value) || 0;
    return sum;
  }
  // Mirrors sicboWeightsSchema (@apx/integrator-config): each face ∈ [0,1] and
  // faces sum to 1.0 (±0.01). Gate the Propose button locally so an invalid
  // table never makes the round-trip just to bounce off the server refine.
  function validity(): { ok: boolean; reason: string } {
    const vals = [...dieInputs.values()].map((i) => Number(i.value) || 0);
    if (vals.some((v) => v < 0 || v > 1)) {
      return { ok: false, reason: 'each face must be between 0 and 1' };
    }
    if (Math.abs(vals.reduce((a, b) => a + b, 0) - 1) >= 0.01) {
      return { ok: false, reason: 'faces must sum to 1.0 (±0.01)' };
    }
    return { ok: true, reason: '' };
  }
  function updateSum(): void {
    const sum = sumOf();
    const v = validity();
    // Face out of range can still sum to ~1 (e.g. 1.5 + −0.5), so surface the
    // real reason inline rather than trusting sum colour alone.
    dieSumEl.textContent = v.ok || v.reason.startsWith('faces must sum')
      ? `sum: ${sum.toFixed(4)}`
      : `sum: ${sum.toFixed(4)} — ${v.reason}`;
    dieSumEl.style.color = v.ok
      ? 'var(--success)'
      : Math.abs(sum - 1) < 0.05 && v.reason.startsWith('faces must sum')
        ? 'var(--warn)'
        : 'var(--error)';
    saveBtn.disabled = !v.ok;
    saveBtn.title = v.ok ? '' : v.reason;
  }

  function fillForm(value: SicboWeightsOverride): void {
    const die = value.die as Record<string, number>;
    for (const [f, input] of dieInputs) input.value = String(die[f] ?? 0);
    updateSum();
  }
  function readForm(): SicboWeightsOverride {
    const die: Record<string, number> = {};
    for (const [f, input] of dieInputs) die[f] = Number(input.value) || 0;
    return { version: 1, die } as unknown as SicboWeightsOverride;
  }

  // Load current effective surface
  const { value: current } = await deps.client.getSurface(deps.integratorId, deps.gameId, 'weights');
  const stored = current as Partial<SicboWeightsOverride> | null;
  if (stored && stored.die) {
    fillForm(stored as SicboWeightsOverride);
  } else {
    fillForm(DEFAULT_WEIGHTS);
  }

  root.querySelector<HTMLButtonElement>('#sw-normalize')!.addEventListener('click', () => {
    const sum = sumOf();
    if (sum <= 0) return;
    for (const input of dieInputs.values()) input.value = ((Number(input.value) || 0) / sum).toFixed(5);
    updateSum();
  });

  root.querySelector<HTMLButtonElement>('#sw-uniform')!.addEventListener('click', () => {
    fillForm(DEFAULT_WEIGHTS);
    msgEl.textContent = 'Reset to fair die (not saved).';
    msgEl.className = 'muted';
  });

  root.querySelector<HTMLButtonElement>('#sw-save')!.addEventListener('click', async () => {
    msgEl.textContent = '';
    const parsed = sicboWeightsSchema.safeParse(readForm());
    if (!parsed.success) {
      msgEl.textContent = parsed.error.errors.map((e) => `${e.path.join('.')}: ${e.message}`).join(' • ');
      msgEl.className = 'error';
      return;
    }
    try {
      const res = await deps.client.proposeSurface(
        deps.integratorId,
        deps.gameId,
        'weights',
        parsed.data as SicboWeightsOverride,
      );
      msgEl.textContent = `Pending approval ${res.approval.id}. A different admin must approve.`;
      msgEl.className = 'muted';
      await refreshPending();
    } catch (err) {
      msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
      msgEl.className = 'error';
    }
  });

  async function refreshPending(): Promise<void> {
    const all = await deps.client.listApprovals(deps.integratorId, 'pending');
    const subset = all.filter((a) => a.surface === 'weights' && a.gameId === deps.gameId);
    const container = root.querySelector<HTMLElement>('#sw-pending')!;
    if (subset.length === 0) {
      container.innerHTML = '<p class="muted">No pending changes.</p>';
      return;
    }
    container.innerHTML = `<table>
      <thead><tr><th>Created</th><th>Proposer</th><th></th></tr></thead>
      <tbody>${subset
        .map(
          (a) => `<tr>
            <td>${fmtDateTime(a.createdAt)}</td>
            <td><code>${a.proposerUserId.slice(0, 8)}…</code></td>
            <td>
              <button data-approve="${a.id}">Approve</button>
              <button data-reject="${a.id}" class="ghost">Reject</button>
            </td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>`;
    container.querySelectorAll<HTMLButtonElement>('button[data-approve]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        try {
          await deps.client.approve(btn.dataset.approve!);
          msgEl.textContent = 'Approved — die weights now live.';
          msgEl.className = 'muted';
          await refreshPending();
          const fresh = await deps.client.getSurface(deps.integratorId, deps.gameId, 'weights');
          const value = fresh.value as Partial<SicboWeightsOverride> | null;
          fillForm(value && value.die ? (value as SicboWeightsOverride) : DEFAULT_WEIGHTS);
        } catch (err) {
          msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
          msgEl.className = 'error';
        }
      });
    });
    container.querySelectorAll<HTMLButtonElement>('button[data-reject]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const reason = await askPrompt('Rejection reason?');
        if (!reason) return;
        try {
          await deps.client.reject(btn.dataset.reject!, reason);
          await refreshPending();
        } catch (err) {
          msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
          msgEl.className = 'error';
        }
      });
    });
  }
  await refreshPending();
}
